import { string, ref, object, number, date, array } from 'yup';
import errorMessages from './errorMessages';

const urlValidationRegex = /^(https?:\/\/)([\w.-]+)(:[0-9]+)?(\/[\w.-]*)*(\?[\w=&%.-]*)?(#[\w.-]*)?$/;
const domainRegex = /^(?:(?:https?:\/\/)?(?:www\.)?)?(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/\S*)?$/


export const createPasswordSchema =
  string()
    .min(8, errorMessages.minLength(8))
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      errorMessages.passwordStrength
    )
    .required(errorMessages.required);

export const confirmPasswordSchema = (passwordFieldName = 'password') =>
  string()
    .oneOf([ref(passwordFieldName), null], errorMessages.passwordMatch)
    .required(errorMessages.required);

export const emailSchema = string().email(errorMessages.email).required(errorMessages.required);

export const otpShema = string().min(6, errorMessages.minLength(6)).required(errorMessages.required);

export const dateRequiredSchema = date().required(errorMessages.required);

export const endDateSchema = date().required(errorMessages.required).min(ref("start_date"), 'Must be greater than start date');

export const stringRequiredSchema = string().required(errorMessages.required);

export const stringOptionalSchema = string().optional().nullable();

export const stringLengthSchema = (length = 1500, requiredMessage = "This field is required") => string().required(requiredMessage).max(length, `Maximum ${length} characters allowed.`)

export const numberRequiredSchema = number().required(errorMessages.required);

export const numberOptionalSchema = number().optional();

export const urlRequiredSchema =
  string()
    .required(errorMessages.required)
    .matches(domainRegex, errorMessages.url)

export const select2RequiredSchema = object().shape({
  value: stringRequiredSchema,
  label: stringRequiredSchema,
}).required('Please select a valid option.');

export const select2OptionalSchema = object().shape({
  value: stringOptionalSchema,
  label: stringOptionalSchema,
}).optional('Please select a valid option.');

export const dateSchema = date().required(errorMessages.required).typeError(errorMessages.date);

export const stringSchema = (required = false, length = 1000) => {
  let schema = string().max(length, errorMessages.maxLength(length));
  if (required) {
    schema = schema.required(errorMessages.required);
  } else {
    schema = schema.nullable();
  }

  return schema;
}

// Word limit validation
export const wordLimitValidation = (limit) =>
  string()
    .trim()
    .required('This field is required.')
    .test('word-count', `Must not exceed ${limit} words.`, (value) => {
      if (!value) return true;
      const wordCount = value.trim().split(/\s+/).length;
      return wordCount <= limit;
    })
    .max(1000);

export const reportQuestionsValidationSchema = (reportQuestions) => {
  let validationSchema = {};

  reportQuestions?.forEach((question) => {
    let schema = {
      answer: string().nullable()
    };

    if (
      question.is_required === 'yes' &&
      question.question_type !== "date" &&
      question.question_type !== "tracker"
    ) {
      schema.answer = schema.answer.required(errorMessages.required);
    }

    if (question.question_type === 'date') {
      schema.other_details = object().shape({
        dates: array().of(
          dateSchema
        )
      })
    }

    if (question.visibility !== "default") {
      schema.answer = string().when(`other_details.visibility`, {
        is: true,
        then: (schema) => schema.required(errorMessages.required),
        otherwise: (schema) => schema.notRequired(),
      })
    }

    validationSchema[question.id] = object().shape(schema);
  });

  return object().shape(validationSchema);
};

export const reportQuestionsValidationSchemaV2 = (reportQuestions) => {
  let validationSchema = {};

  reportQuestions?.forEach((question) => {
    let schema = {
      answer: string().nullable()
    };

    if (
      question.is_required === 'yes' &&
      question.question_type !== "date" &&
      question.question_type !== "tracker"
    ) {
      schema.answer = schema.answer.required(errorMessages.required);
    }

    if (question.question_type === 'date') {
      schema.other_details = object().shape({
        dates: array().of(
          dateSchema
        )
      })
    }

    if (question.visibility !== "default") {
      schema.answer = string().when(`other_details.visibility`, {
        is: true,
        then: (schema) => schema.required(errorMessages.required),
        otherwise: (schema) => schema.notRequired(),
      })
    }

    validationSchema[question.id] = object().shape(schema);
  });

  return object().shape(validationSchema);
};

export const genericFormErrorMsg = "There are errors in the form. Please review your entries and try again.";

export const phoneRequiredSchema =
  string()
    .required(errorMessages.required)
    .matches(/^\+?\s*\d+(\s*\d+)*$/, errorMessages.phone)
    .min(8, errorMessages.minDigits(7));